import React, { memo, useCallback, useState } from 'react';

import { Box, IconButton, Tooltip } from '@mui/material';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import { AiChatTestIds } from '../../AiChat.testIds';
import { PaperPlaneIcon } from '../../icons';

import {
  INPUT_MAX_ROWS,
  INPUT_MIN_ROWS,
  SEND_BUTTON_TOOLTIP,
  STOP_BUTTON_TOOLTIP,
} from './AiChatInput.constants';
import {
  inputContainerStyles,
  inputFieldStyles,
  sendButtonStyles,
  stopButtonStyles,
  stopIconSquareStyles,
} from './AiChatInput.styles';

const AiChatInput = ({ onSend, onStop, disabled = false, isInFlight = false }) => {
  const [inputValue, setInputValue] = useState('');

  const trimmedValue = inputValue.trim();
  const hasText = !!trimmedValue;
  const isStopMode = isInFlight && !hasText;

  const onInputChange = useCallback(e => {
    setInputValue(e.target.value);
  }, []);

  const onSendClick = useCallback(() => {
    if (!trimmedValue) {
      return;
    }

    onSend(trimmedValue);
    setInputValue('');
  }, [trimmedValue, onSend]);

  const onKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onSendClick();
      }
    },
    [onSendClick],
  );

  return (
    <Box data-testid={AiChatTestIds.AI_CHAT_INPUT} sx={inputContainerStyles}>
      <TextFieldCDS
        fullWidth
        multiline
        dataTestId={AiChatTestIds.AI_CHAT_INPUT_FIELD}
        disabled={disabled}
        maxRows={INPUT_MAX_ROWS}
        name="aiChatInput"
        placeholder="Ask me anything..."
        rows={INPUT_MIN_ROWS}
        sx={inputFieldStyles}
        value={inputValue}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
      />
      <Tooltip title={isStopMode ? STOP_BUTTON_TOOLTIP : SEND_BUTTON_TOOLTIP}>
        <span>
          <IconButton
            data-testid={
              isStopMode
                ? AiChatTestIds.AI_CHAT_INPUT_STOP_BUTTON
                : AiChatTestIds.AI_CHAT_INPUT_SEND_BUTTON
            }
            disabled={!isStopMode && !hasText}
            sx={isStopMode ? stopButtonStyles : sendButtonStyles}
            onClick={isStopMode ? onStop : onSendClick}
          >
            {isStopMode ? <Box sx={stopIconSquareStyles} /> : <PaperPlaneIcon size={16} />}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

AiChatInput.propTypes = {
  onSend: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isInFlight: PropTypes.bool,
  onStop: PropTypes.func,
};

export default memo(AiChatInput);
